import {
    Comment,
    Delete,
    Favorite,
    FavoriteBorder,
    Send,
} from '@mui/icons-material';
import {
    Avatar,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Grid,
    IconButton,
    OutlinedInput,
    Typography,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
    useCreateCommetMutation,
    useDeleteCommentMutation,
    useDeleteFeedMutation,
    useGetFeedInfoQuery,
    useLoadFeedCommentsQuery,
} from '../../api/apiSlice';
import { useToggleLikeMutation } from '../../api/feedSlice';
import Screen from '../../components/fragments/Screen';

export default function FeedRouteScreen() {
    const { id } = useParams();

    const theme = useTheme();
    const navigate = useNavigate();
    const state = useSelector((st) => st);

    const [toggleLike] = useToggleLikeMutation();
    const [deleteFeed] = useDeleteFeedMutation();

    const { data, isFetching } = useGetFeedInfoQuery(id, {
        refetchOnMountOrArgChange: true,
    });

    const feed = data?.data;

    const { user } = state.auth;
    const { dates, interactions, media, member, post } = feed || {};

    const [liked, setLiked] = useState(interactions?.liked);
    const [likes, setLikes] = useState(interactions?.likes);

    const onLikeFeed = async () => {
        await toggleLike({ id, type: 'posts' });
        liked ? setLikes(likes - 1) : setLikes(likes + 1);
        setLiked(!liked);
    };

    const onDeleteFeed = async () => {
        await deleteFeed(id);
        navigate(-1);
    };

    return (
        <Screen loading={isFetching} title="Feed">
            <Grid item xs={12}>
                {!isFetching && dates && (
                    <div>
                        <CardHeader
                            title={member?.name}
                            subheader={moment(dates?.updated).fromNow()}
                            avatar={
                                <Avatar
                                    src={member?.avatar}
                                    aria-label="profile"
                                >
                                    {member?.name?.slice(0, 2)}
                                </Avatar>
                            }
                        />
                        <CardContent className="py-0">
                            {post}
                            {media
                                ?.filter(({ type }) => type === 'image')
                                .map(({ link }) => (
                                    <img
                                        key={link}
                                        className="mt-2 w-100"
                                        src={link}
                                        alt={member?.name}
                                        style={{
                                            borderRadius:
                                                theme.shape.borderRadius,
                                            border:
                                                theme.palette.mode == 'light' &&
                                                `2px solid #ddd`,
                                            background:
                                                theme.palette.mode === 'light'
                                                    ? '#ddd'
                                                    : theme.palette.background
                                                          .paper,
                                        }}
                                    />
                                ))}
                            {media
                                ?.filter(({ type }) => type === 'video')
                                .map(({ link }) => (
                                    <video
                                        key={link}
                                        className="mt-2"
                                        controls="controls"
                                        preload="none"
                                        width="100%"
                                    >
                                        <source
                                            id="mp4"
                                            src={link}
                                            type="video/mp4"
                                        />
                                    </video>
                                ))}
                        </CardContent>
                        <CardActions className="justify-content-between">
                            <Button
                                color="secondary"
                                size="medium"
                                startIcon={
                                    liked ? <Favorite /> : <FavoriteBorder />
                                }
                                onClick={onLikeFeed}
                            >
                                {likes || '0'}
                            </Button>
                            <Button size="medium" startIcon={<Comment />}>
                                {interactions?.comments || '0'}
                            </Button>
                            {feed?.member?.id == user.id && (
                                <Button
                                    size="medium"
                                    className="text-danger text-capitalize"
                                    startIcon={<Delete />}
                                    onClick={onDeleteFeed}
                                >
                                    Delete
                                </Button>
                            )}
                        </CardActions>
                        <FeedComments postId={id} />
                    </div>
                )}
            </Grid>
        </Screen>
    );
}

const FeedComments = ({ postId }) => {
    const [commentText, setCommentText] = useState('');

    const state = useSelector((st) => st);
    const {
        data,
        isFetching,
        refetch: refetchComments,
    } = useLoadFeedCommentsQuery(postId, {
        refetchOnMountOrArgChange: true,
    });
    const [createComment] = useCreateCommetMutation();
    const [deleteComment] = useDeleteCommentMutation();

    const comments = data?.data;
    const { user } = state.auth;

    const onCreateComment = async () => {
        await createComment({ id: postId, comment: commentText });
        refetchComments();
        setCommentText('');
    };

    const onDeleteComment = async (id) => {
        await deleteComment({ commentId: id, postId });
    };

    return (
        <>
            <CardActions>
                <Avatar src={user?.links?.avatar}>
                    {user?.firstName?.slice(0, 2)}
                </Avatar>
                <OutlinedInput
                    fullWidth
                    className="ms-2"
                    placeholder="Write a comment"
                    onChange={(e) => setCommentText(e.target.value)}
                    value={commentText}
                />
                <IconButton
                    disabled={commentText.length < 2 || isFetching}
                    onClick={onCreateComment}
                >
                    <Send />
                </IconButton>
            </CardActions>
            <CardContent>
                {comments?.map(
                    ({
                        id,
                        dates: commentDates,
                        member: commentMember,
                        post: commentPost,
                    }) => (
                        <div
                            className="d-flex align-items-start w-100 mb-2"
                            key={id}
                        >
                            <Avatar src={commentMember?.avatar}>
                                {commentMember?.name?.slice(0, 2)}
                            </Avatar>
                            <Card variant="outlined" className="ms-2 w-100">
                                <div className="m-2">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <Typography
                                            className="fw-bold"
                                            color="primary"
                                        >
                                            {commentMember?.name}
                                        </Typography>
                                        {commentMember.id == user?.id && (
                                            <Button
                                                disabled={isFetching}
                                                className="text-capitalize text-danger"
                                                size="small"
                                                onClick={() =>
                                                    onDeleteComment(id)
                                                }
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </div>
                                    <Typography>{commentPost} </Typography>
                                    <Typography
                                        color="textSecondary"
                                        variant="body2"
                                    >
                                        {moment(
                                            commentDates?.updated
                                        ).fromNow()}
                                    </Typography>
                                </div>
                            </Card>
                        </div>
                    )
                )}
            </CardContent>
        </>
    );
};
